import React from 'react';
import { Modal } from '../ui/Modal.js';
import translations from '../../constants/translations.json';
import { useDataContext } from '../../context/data.js';

// Shown when preorderv2 returns 429 (capacity reached). Two flavours:
// - Take-away: the user can recover by picking another time → CTA fires
//   onPickAnotherTime (caller routes back to the time-picker step) or
//   falls back to onClose. Surfaces nextAvailableSlot when present.
// - Dine-in: the user is sitting in the restaurant, they can't shop a
//   different time. Different copy + a single "Got it" acknowledgement.
const CapacityFullModal = ({
  isOpen,
  onClose,
  nextAvailableSlot,
  isDineIn = false,
  onPickAnotherTime,
}) => {
  const { language } = useDataContext();

  const t = (key, fallback) => translations[language]?.[key] || fallback;

  // Backend returns date as MM/DD/YYYY (carryover from the admin API).
  // Rewrite to European DD/MM/YYYY for the guest-facing modal — the app is
  // primarily Scandinavian and showing month-first confused operators.
  const formatNext = () => {
    if (!nextAvailableSlot || !nextAvailableSlot.slotStart) return null;
    const time = nextAvailableSlot.slotStart;
    const raw = nextAvailableSlot.date || '';
    let date = raw;
    const mmddyyyy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mmddyyyy) date = `${mmddyyyy[2]}/${mmddyyyy[1]}/${mmddyyyy[3]}`;
    return `${time}${date ? ` · ${date}` : ''}`;
  };
  const nextLabel = formatNext();

  const title = isDineIn
    ? t('capacity_modal_dinein_title', 'The restaurant is too busy')
    : t('capacity_modal_title', 'It is busy at this time');

  const body = isDineIn
    ? t(
        'capacity_modal_dinein_body',
        'We cannot take more orders right now. Please ask a member of staff or try again in a moment.',
      )
    : t(
        'capacity_modal_body',
        'The restaurant is too busy to accept more orders at the time you selected. Please pick another time — we can still take your order.',
      );

  const ctaLabel = isDineIn
    ? t('capacity_modal_dinein_cta', 'Got it')
    : t('capacity_modal_cta', 'Pick another time');

  const handleCta = () => {
    if (!isDineIn && typeof onPickAnotherTime === 'function') {
      onPickAnotherTime();
    } else {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClassName="max-w-[400px]">
      <div style={{ padding: '32px 24px 24px' }}>
        <div className="text-center" style={{ marginBottom: 20 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fb923c, #ef4444)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 4px 16px rgba(239,68,68,0.3)',
            }}
          >
            <span style={{ fontSize: 32 }}>{isDineIn ? '🍽️' : '⏱️'}</span>
          </div>
          <h3
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#1a1a1a',
              marginBottom: 6,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              fontSize: 14,
              color: '#555',
              lineHeight: '1.5',
              marginBottom: 0,
            }}
          >
            {body}
          </p>
        </div>

        {nextLabel && (
          <div
            style={{
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 16,
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#b45309',
                marginBottom: 2,
              }}
            >
              {isDineIn
                ? t('capacity_modal_dinein_next_open', 'Open for orders again')
                : t('capacity_modal_next_available', 'Next available time')}
            </p>
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#1a1a1a',
                marginBottom: 0,
              }}
            >
              {nextLabel}
            </p>
          </div>
        )}

        <button
          onClick={handleCta}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: 15,
            fontWeight: 600,
            background: '#1a1a1a',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#333')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#1a1a1a')}
        >
          {ctaLabel}
        </button>
      </div>
    </Modal>
  );
};

export default CapacityFullModal;
